import React from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, ShieldAlert, FileSignature, CheckCircle2, RefreshCw } from "lucide-react";

import { Layout } from "@/components/Layout";
import { PageHeader } from "@/components/PageHeader";
import { getTermsAccess, getZohoStatus } from "@/lib/api";

const POLL_INTERVAL_MS = 5000;

export default function Terms() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");

  const [state, setState] = React.useState("loading"); // loading | ready | error | signed
  const [signUrl, setSignUrl] = React.useState("");
  const [errorMsg, setErrorMsg] = React.useState("");
  const pollRef = React.useRef(null);

  const goToOnboarding = React.useCallback(() => {
    navigate(`/onboarding?token=${encodeURIComponent(token)}`);
  }, [navigate, token]);

  // Validate the token and load a fresh signing URL.
  React.useEffect(() => {
    let cancelled = false;
    if (!token) {
      setState("error");
      setErrorMsg("This link is missing its session token. Please use the link from your onboarding flow.");
      return;
    }
    (async () => {
      try {
        const data = await getTermsAccess(token);
        if (cancelled) return;
        if (data.status === "terms_signed" || data.status === "onboarding_complete") {
          setState("signed");
          setTimeout(goToOnboarding, 1200);
          return;
        }
        setSignUrl(data.sign_url);
        setState("ready");
      } catch (e) {
        if (cancelled) return;
        setState("error");
        setErrorMsg(
          e?.response?.data?.detail ||
            "We couldn't load your agreement. Please try again shortly."
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, goToOnboarding]);

  // Poll for signing completion while the iframe is shown.
  React.useEffect(() => {
    if (state !== "ready" || !token) return;
    pollRef.current = setInterval(async () => {
      try {
        const s = await getZohoStatus(token);
        if (s.status === "terms_signed" || s.status === "onboarding_complete") {
          clearInterval(pollRef.current);
          setState("signed");
          setTimeout(goToOnboarding, 1500);
        }
      } catch (e) {
        // Transient polling errors are ignored; we keep trying.
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [state, token, goToOnboarding]);

  return (
    <Layout currentStep={2}>
      <PageHeader
        step={2}
        title="Terms"
        subtitle="Review your agreement and sign it securely below. Your progress is saved automatically once you finish."
      />

      {state === "loading" && (
        <div
          data-testid="terms-loading"
          className="bg-[#121212] rounded-2xl border border-white/10 p-16 shadow-2xl shadow-black/50 flex flex-col items-center justify-center animate-fade-slide-up"
        >
          <Loader2 className="h-8 w-8 text-[#F5C518] animate-spin" />
          <p className="mt-4 font-body text-sm text-zinc-400">Verifying your secure link…</p>
        </div>
      )}

      {state === "error" && (
        <div
          data-testid="terms-error"
          className="bg-[#121212] rounded-2xl border border-red-500/25 p-10 sm:p-14 shadow-2xl shadow-black/50 text-center animate-fade-slide-up"
        >
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-red-500/10 border border-red-500/30">
            <ShieldAlert className="h-7 w-7 text-red-400" />
          </div>
          <h2 className="mt-6 font-heading text-2xl font-semibold tracking-tight">
            We can't open this agreement
          </h2>
          <p data-testid="terms-error-message" className="mt-3 font-body text-sm text-zinc-400 max-w-md mx-auto leading-relaxed">
            {errorMsg}
          </p>
          <button
            data-testid="terms-restart-btn"
            onClick={() => navigate("/")}
            className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/15 bg-transparent text-white px-6 h-11 hover:bg-white/5 hover:border-white/25 active:bg-white/10 transition-colors duration-200 touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F5C518]/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0a0a]"
          >
            <RefreshCw className="h-4 w-4" />
            Start over
          </button>
        </div>
      )}

      {state === "signed" && (
        <div
          data-testid="terms-signed"
          className="bg-[#121212] rounded-2xl border border-white/10 p-14 shadow-2xl shadow-black/50 text-center animate-fade-slide-up"
        >
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#F5C518]/10 border border-[#F5C518]/30">
            <CheckCircle2 className="h-7 w-7 text-[#F5C518]" />
          </div>
          <h2 className="mt-6 font-heading text-2xl font-semibold tracking-tight">Agreement signed</h2>
          <p className="mt-3 font-body text-sm text-zinc-400">Taking you to the final step…</p>
        </div>
      )}

      {state === "ready" && (
        <div
          data-testid="terms-card"
          className="bg-[#121212] rounded-2xl border border-white/10 shadow-2xl shadow-black/50 overflow-hidden animate-fade-slide-up"
        >
          <div className="flex flex-wrap items-center gap-2 px-4 sm:px-6 py-4 border-b border-white/10">
            <FileSignature className="h-5 w-5 text-[#F5C518]" />
            <h2 className="font-heading text-base font-semibold tracking-tight">Your Agreement</h2>
            <span className="ml-auto inline-flex items-center gap-1.5 text-xs text-zinc-500">
              <span className="h-1.5 w-1.5 rounded-full bg-[#F5C518] animate-pulse" />
              Waiting for signature
            </span>
          </div>
          <iframe
            data-testid="terms-iframe"
            title="Zoho Sign Agreement"
            src={signUrl}
            className="w-full h-[70vh] min-h-[460px] sm:h-[75vh] sm:min-h-[560px] bg-white"
            allow="camera; microphone; fullscreen"
          />
        </div>
      )}
    </Layout>
  );
}
